import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Package2, Archive, FileText } from "lucide-react";
import { exportBrandedPDF } from "../../lib/pdfExport";

const CONTAINER_LABEL = { festgebunden: "Festgebunden", schwachgebunden: "Schwachgebunden" };
const CONTAINER_COLOR = { festgebunden: "#CA8A04", schwachgebunden: "#DC2626" };
const BAG_KG = 14;

export function Containers() {
  const [containers, setContainers] = useState([]);
  const [history, setHistory] = useState([]);
  const [emptyTarget, setEmptyTarget] = useState(null);

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/containers"),
      api.get("/container-fills?limit=20"),
    ]);
    setContainers(c.data || []);
    setHistory(h.data || []);
  };
  useEffect(() => { load(); }, []);

  const fmtTs = (s) => {
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
    } catch { return s; }
  };

  const confirmEmpty = async () => {
    if (!emptyTarget) return;
    try {
      const { data } = await api.post(`/containers/${emptyTarget.type}/empty`);
      toast.success(`Container ${CONTAINER_LABEL[emptyTarget.type]} golit · ${data.total_bags} saci · ${data.total_weight_kg} kg`);
      setEmptyTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const exportContainerPDF = async (snapshot, isCurrent = false) => {
    const label = CONTAINER_LABEL[snapshot.type];
    const periodFrom = snapshot.since_iso;
    const periodTo = snapshot.emptied_at || new Date().toISOString();
    const breakdown = snapshot.breakdown || [];
    try {
      await exportBrandedPDF({
        title: `Raport container ${label}`,
        subtitle: isCurrent ? "Stadiu curent (container neumplut/neevacuat)" : `Snapshot golire ${fmtTs(snapshot.emptied_at)}`,
        sections: [
          {
            heading: "Sumar",
            summary: [
              ["Tip container", label],
              ["Perioadă", `${fmtTs(periodFrom)} → ${fmtTs(periodTo)}`],
              ["Total saci", String(snapshot.total_bags || 0)],
              ["Greutate estimată", `${snapshot.total_weight_kg || 0} kg (≈ ${BAG_KG} kg/sac)`],
              ...(snapshot.emptied_by_name ? [["Golit de", snapshot.emptied_by_name]] : []),
            ],
          },
          {
            heading: "Detaliu pe șantiere",
            table: {
              columns: ["Șantier", "Adresă", "Intrări", "Saci", "Kg"],
              rows: breakdown.length === 0
                ? [["—", "container gol", "0", "0", "0"]]
                : breakdown.map((b) => [b.project_name, b.project_address || "—", String(b.entries), String(b.bags), String(b.weight_kg)]),
            },
          },
        ],
        filename: `container-${snapshot.type}-${(snapshot.emptied_at || new Date().toISOString()).slice(0, 10)}.pdf`,
      });
      toast.success("PDF generat");
    } catch (e) { toast.error("Eroare generare PDF"); }
  };

  return (
    <div className="space-y-6" data-testid="containers-view">
      <div className="text-xs text-zinc-500">Fiecare sac cântărește aproximativ <strong>{BAG_KG} kg</strong>. Containerele se resetează automat când le marchezi „golit".</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {containers.map((c) => (
          <div key={c.type} className="card-flat p-5" data-testid={`container-${c.type}`} style={{ borderLeft: `4px solid ${CONTAINER_COLOR[c.type]}` }}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md grid place-items-center" style={{ background: CONTAINER_COLOR[c.type] + "1A", color: CONTAINER_COLOR[c.type] }}>
                  <Package2 size={22}/>
                </div>
                <div>
                  <div className="font-display text-lg font-bold">{CONTAINER_LABEL[c.type]}</div>
                  <div className="text-xs text-zinc-500">de la {fmtTs(c.since_iso)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                onClick={() => setEmptyTarget(c)}
                disabled={c.total_bags === 0}
                data-testid={`empty-${c.type}`}
                style={c.total_bags > 0 ? { borderColor: CONTAINER_COLOR[c.type], color: CONTAINER_COLOR[c.type] } : {}}
              >
                <Archive size={14} className="mr-1.5"/> Marchează golit
              </Button>
              <Button
                variant="outline"
                onClick={() => exportContainerPDF(c, true)}
                disabled={c.total_bags === 0}
                data-testid={`pdf-${c.type}`}
                title="Export PDF stadiu curent"
              >
                <FileText size={14} className="mr-1.5"/> PDF
              </Button>
            </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="card-flat p-3 text-center">
                <div className="small-label">Saci în container</div>
                <div className="font-display text-3xl font-extrabold mt-1" style={{ color: CONTAINER_COLOR[c.type] }} data-testid={`${c.type}-bags`}>{c.total_bags}</div>
              </div>
              <div className="card-flat p-3 text-center">
                <div className="small-label">Greutate estimată</div>
                <div className="font-display text-3xl font-extrabold mt-1 text-zinc-700" data-testid={`${c.type}-weight`}>{c.total_weight_kg} <span className="text-sm font-normal text-zinc-500">kg</span></div>
              </div>
            </div>

            <div className="small-label mb-2">Detaliu pe șantiere ({c.breakdown.length})</div>
            {c.breakdown.length === 0 ? (
              <div className="text-xs text-zinc-400 italic py-2">Container gol — niciun sac de la ultima golire.</div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {c.breakdown.map((b) => (
                  <div key={b.project_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{b.project_name}</div>
                      {b.project_address && <div className="text-[10px] text-zinc-500 truncate">{b.project_address}</div>}
                    </div>
                    <span className="text-xs font-mono text-zinc-500">{b.entries} {b.entries === 1 ? "intrare" : "intrări"}</span>
                    <span className="text-sm font-bold tabular-nums" style={{ color: CONTAINER_COLOR[c.type] }}>{b.bags} saci</span>
                    <span className="text-xs text-zinc-500 tabular-nums w-16 text-right">{b.weight_kg} kg</span>
                  </div>
                ))}
              </div>
            )}

            {c.last_emptied_at && (
              <div className="text-[10px] text-zinc-400 mt-3 pt-2 border-t border-[var(--border)]">
                Ultima golire: {fmtTs(c.last_emptied_at)} de {c.last_emptied_by}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card-flat p-5">
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <Archive size={16} className="text-zinc-500"/> Istoric goliri
        </h3>
        {history.length === 0 ? (
          <div className="text-sm text-zinc-400 italic">Niciun container golit încă.</div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <details key={h.id} className="border border-[var(--border)] rounded-md p-2 hover:bg-zinc-50">
                <summary className="cursor-pointer flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-sm" style={{ background: CONTAINER_COLOR[h.type] }}/>
                  <span className="font-semibold">{CONTAINER_LABEL[h.type]}</span>
                  <span className="text-zinc-500 text-xs">{fmtTs(h.emptied_at)}</span>
                  <span className="ml-auto font-mono">{h.total_bags} saci · {h.total_weight_kg} kg</span>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); exportContainerPDF(h, false); }}
                    className="text-[var(--primary)] hover:bg-[var(--primary-subtle)] p-1 rounded"
                    title="Export PDF"
                    data-testid={`pdf-history-${h.id}`}
                  >
                    <FileText size={13}/>
                  </button>
                </summary>
                <div className="mt-2 pl-5 space-y-1 text-xs">
                  <div className="text-zinc-500">Golit de: <strong>{h.emptied_by_name}</strong> · perioadă: {fmtTs(h.since_iso)} → {fmtTs(h.emptied_at)}</div>
                  {(h.breakdown || []).map((b) => (
                    <div key={b.project_id} className="flex items-center gap-2">
                      <span className="flex-1 truncate">{b.project_name}</span>
                      <span className="font-bold">{b.bags} saci</span>
                      <span className="text-zinc-500 w-16 text-right">{b.weight_kg} kg</span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      {emptyTarget && (
        <Dialog open={true} onOpenChange={(v) => !v && setEmptyTarget(null)}>
          <DialogContent className="max-w-lg" data-testid="empty-dialog">
            <DialogHeader>
              <DialogTitle>Confirmare golire · {CONTAINER_LABEL[emptyTarget.type]}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>Vei închide actuala umplere a containerului și se va salva în istoric următoarea repartizare:</p>
              <div className="card-flat p-3 bg-zinc-50">
                <div className="flex items-center justify-between mb-2">
                  <strong>Total: {emptyTarget.total_bags} saci</strong>
                  <strong>{emptyTarget.total_weight_kg} kg</strong>
                </div>
                <div className="space-y-1 text-xs">
                  {emptyTarget.breakdown.map((b) => (
                    <div key={b.project_id} className="flex justify-between">
                      <span>{b.project_name}</span>
                      <span>{b.bags} saci · {b.weight_kg} kg</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-zinc-500">După confirmare, contorul se resetează la 0 și începe o nouă perioadă de tracking.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmptyTarget(null)}>Anulează</Button>
              <Button onClick={confirmEmpty} data-testid="empty-confirm" className="bg-[var(--primary)]">Confirmă golirea</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
